import * as React from "react";
import {cx} from "emotion";

import "./change-listing-modal.scss";

import {XdSentry}       from "@Common/libs/sentry/xd-sentry";
import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {UseState}       from "@Common/react/use-state";
import {formatDate}     from "@Common/ui-components/format-date";
import {isEmpty}        from "@Common/utils/objects";
import {ModalService3}  from "@Common/ui-components/modal-service-3/modal-service-3";

import {formatFlexMoney}      from "../../../../../my-listings/card/format-flex-money";
import {ImageGallery}         from "../../../../../my-listings/common/image-gallery/image-gallery";
import {InfiniteLoader}       from "../../../../../my-listings/common/infinite-loader";
import {EmptyListings}        from "../../../../../my-listings/empty-listings";
import {MyListingsErrorModal} from "../../../../../my-listings/my-listings-error-modal/my-listings-error-modal";
import {MyListingsLoading}    from "../../../../../my-listings/my-listings-loading";
import {SearchOptions}        from "../../../../../my-listings/search-options/search-options";
import {ScreenLoader}         from "@Common/ui-components/loading-spinners/screen-loader";
import {CustomizationLostWarningModal} from "./customization-lost-warning-modal/customization-lost-warning-modal";

export const ChangeListingModal = ({title, listingId, getItem, host, stateId, orderId, clearChanged, isBuild3=false, isListingsEligible = true, next: rootNext}) => cs(

    consumeContext("apis"),
    consumeContext("ccApis"),
    consumeContext("company"),
    consumeContext("session"),
    consumeContext("userLevel"),
    ["modal", ({}, next) => ModalService3({
        xdQaName: "Change Listing Modal",
        rootNext,
        next,
        xToClose: false,
        width: 650,
        preventBackgroundScroll: true,
    })],
    ["searchOptions", (_, next) => SearchOptions({isCC:true, next})],
    ["loader", ({searchOptions, session, apis, company}, next) => InfiniteLoader({
        // step: 10,
        key: JSON.stringify({
            filter: searchOptions.options.filter,
            emulation: session.value,
            sorting: searchOptions.options.sorting,
        }),

        getTotalCount: (result) => result.totalCount,
        getList: (result) => result.records,
        fetchRange: async (range) => await apis.listings.getMyListings({
            filter : searchOptions.options.filter,
            emulation: session.value,
            sorting: searchOptions.options.sorting,
            skip: range.from,
            limit: range.to-range.from,
            allListingsSearch: company?.prefs?.allowUserLevelAllListingsSearch,
        }),
        next,
    })],
    ["selectedListingId", ({}, next) => UseState({
        next: ({value, onChange}) => next({
            value: value ?? listingId,
            onChange,
        }),
    })],
    ["resetOrder", ({}, next) => UseState({
        initValue: false,
        next,
    })],
    ["startNewOrder", ({apis, ccApis, selectedListingId, modal}, next) => next(async () => {
        // clear the selection quickly so modal buttons are disabled
        let listingId = selectedListingId.value;
        selectedListingId.onChange(null);

        let item = await getItem();
        let postgresListing = await apis.listings.getListing({idUnique: listingId});
        item.listingUniqueId = listingId;
        clearChanged();
        await apis.next.gallery.orders.deleteListingImages({orderId});
        if(!isBuild3 && item?.metaData?.ccReference){
            item.metaData.oldCcReference = item.metaData.ccReference;
            delete item.metaData.ccReference;
            await apis.next.item.updateItem(item);
        }
        await apis.next.item.updateItem(item);

        // if there is existing PropertyWizardData we need to update it to match newly selected listing
        let propertyWizardData = await apis.next.gpt.getPropertyWizardData(orderId);
        if (propertyWizardData) {
            let propertyData = {
                // cannot find "event" in listing data
                event: "",
                beds: postgresListing?.bedrooms ?? "",
                baths: postgresListing?.bathrooms ?? "",
                squareFootage: postgresListing?.lotSizeSquareFeet ?? "",
                price: postgresListing?.listedPrice ?? "",
                description: postgresListing?.description ?? "",
                street: postgresListing?.location?.streetAddress ?? "",
                line2: postgresListing?.location?.unitNumber ?? "",
                city: postgresListing?.location?.city ?? "",
                state: postgresListing?.location?.state ?? "",
                zip: postgresListing?.location?.zip ?? "",
                additionalContext: "",
                aggregateSummary: "",
            };
            await apis.next.gpt.savePropertyWizardData({
                idOrder: parseInt(orderId),
                data: propertyData,
            });
        }

        isBuild3 && isListingsEligible && await apis.next.OrderManager.applyListingData(orderId, postgresListing);
        modal.close();
        window.location.reload();
    })],
    ["showLoadingSpinner", ({}, next) => UseState({
        initValue: false,
        next,
    })],
    ["myListingsErrorModal", (_, next) => MyListingsErrorModal({isCC:true, next})],
    ["warningModal", ({startNewOrder, showLoadingSpinner}, next) => CustomizationLostWarningModal({startNewOrder, showLoadingSpinner, next})],
    ["loadingStatus", (_, next) => MyListingsLoading({next})],
    ({apis, ccApis, startNewOrder, warningModal, selectedListingId, loader, searchOptions, myListingsErrorModal, loadingStatus, showLoadingSpinner, modal}) => (
        <div className="change-listing-modal-42t">
            <div className="header">
                <div className="box" xd-qa-type="button" xd-qa-name="Change Listing">
                    <div className="change-listing">
                        <div className="text">
                            Change Listing
                        </div>
                        <i className="fa fa-caret-up"/>
                    </div>
                    <div className="title">
                        {title}
                    </div>
                </div>
            </div>

            <div className="main">
                {(loadingStatus.active || showLoadingSpinner.value) ? (
                    <div className="center">
                        {loadingStatus.message ?? <div className="reloadingOrder"><ScreenLoader/> ...reloading order</div>}
                    </div>
                ) : <>{searchOptions.render({totalCount: loader.totalCount})}
                    <div className="list">
                        {!loader.loading && !searchOptions.options.filter && !loader.list[0] ? (
                            <EmptyListings/>
                        ) : !loader.loading && !loader.list[0] ? (
                            <div className="no-result">
                                No record(s) found
                            </div>
                        ) : <>
                            {loader.list.map((listing) => {
                                const selected = listing?.idUnique == selectedListingId.value;
                                return (
                                    <div
                                        key={listing.id}
                                        className={cx("cc-view-card-yz2 listing", {selected})}
                                        onClick={() => selectedListingId.onChange(listing.idUnique)}
                                        xd-qa-type="button"
                                        xd-qa-name={"Select Listing - " + listing.idUnique}
                                    >
                                        {selected && (
                                            <div className="selected-indicator">
                                                <i className="fa fa-check"/>
                                            </div>
                                        )}
                                        <div className="image-section">
                                            <div className="address">
                                                {listing.streetAddress}
                                                <br/>
                                                {listing.city}, {listing.state} {listing.zip}
                                                <div className="mls-id">
                                                    <label>ID:</label>
                                                    <span>{listing.mlsId}</span>
                                                </div>
                                            </div>

                                            <ImageGallery {...{pictures: listing.thumbPictures}}/>
                                        </div>
                                        <div className="horizontal-divider"/>
                                        <div className="info">
                                            <div className="col">
                                                <div className="label">Price:</div>
                                                <div className="value">{formatFlexMoney(listing.listedPrice)}</div>
                                            </div>
                                            <div className="col">
                                                <div className="label">Date:</div>
                                                <div className="value">
                                                    Listed:{" "}
                                                    {formatDate(listing.dateListed)}
                                                    <br/>
                                                    {listing.dateSold && (<>
                                                        Sold: {formatDate(listing.dateSold)}
                                                    </>)}
                                                </div>
                                            </div>
                                            <div className="col">
                                                <div className="label">Agent:</div>
                                                <div className="value">{listing.agentFirstName} {listing.agentLastName}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </>}
                        {loader.hasMore && (
                            <div className="load-more">
                                <button
                                    onClick={() => loader.loadMore()}
                                    xd-qa-type="button"
                                    xd-qa-name="Load More"
                                >
                                    Load More
                                    {" "}
                                    {loader.loading && (
                                        <i className="fa fa-spinner fa-pulse"/>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </>}
            </div>

            <div className="footer">
                <button
                    className="cancel"
                    onClick={() => modal.close()}
                    xd-qa-type="button"
                    xd-qa-name="Cancel"
                >
                    Cancel
                </button>

                <button
                    className="save"
                    disabled={listingId == selectedListingId.value}
                    onClick={async () => {
                        let postgresListing = await apis.listings.getListing({idUnique: selectedListingId.value});
                        if(isEmpty(postgresListing)) {
                            myListingsErrorModal.show();
                            try {
                                XdSentry.addBreadcrumb({
                                    category: "listing",
                                    message: "Listing with ID: " + selectedListingId.value + " did not load from postgres",
                                    level: XdSentry.Severity.Error,
                                    data: postgresListing
                                });
                                XdSentry.captureMessage("Listing Error", XdSentry.Severity.Log);
                            } catch (error) {
                                console.log('There was an error attempting to log listing to sentry: ' + error.toString() );
                            }
                        }
                        else if (isListingsEligible) {
                            warningModal.show();
                        }
                        else {
                            await startNewOrder();
                        }
                    }}
                    xd-qa-type="button"
                    xd-qa-name="Save & Continue"
                >
                    Save & Continue
                </button>
            </div>
        </div>
    )
);
